import React from "react";
import Box from "@mui/material/Box";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { createAudioAnalyser, type LocalAudioTrack, type RemoteAudioTrack } from "livekit-client";

const SEGMENT_COUNT = 10;
const TRACK_NOISE_FLOOR = 0.008;
const TRACK_LEVEL_GAIN = 9;
const TRACK_LEVEL_SMOOTHING = 0.22;

// Color for each segment index (green -> yellow -> red)
function getSegmentColor(index: number): string {
    if (index < 5) return '#22C55E';
    if (index < 7) return '#84CC16';
    if (index < 9) return '#F59E0B';
    return '#EF4444';
}

interface ParticipantAudioLevelMeterProps {
    level?: number;
    microphoneEnabled?: boolean;
    audioTrack?: LocalAudioTrack | RemoteAudioTrack | null;
}

const ParticipantAudioLevelMeter: React.FC<ParticipantAudioLevelMeterProps> = ({
    level = 0,
    microphoneEnabled = true,
    audioTrack = null,
}) => {
    const [localTrackLevel, setLocalTrackLevel] = React.useState(0);
    const trackMediaStream = audioTrack?.mediaStream;

    React.useEffect(() => {
        if (!audioTrack || !trackMediaStream || !microphoneEnabled) {
            setLocalTrackLevel(0);
            return;
        }

        let intervalId = 0;
        let cleanup: (() => Promise<void>) | null = null;
        let smoothedLevel = 0;

        const start = async () => {
            try {
                const analyserHandle = createAudioAnalyser(audioTrack, {
                    fftSize: 256,
                    smoothingTimeConstant: 0,
                });
                cleanup = analyserHandle.cleanup;
                const bufferLength = analyserHandle.analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);

                const updateLevel = () => {
                    analyserHandle.analyser.getByteTimeDomainData(dataArray);
                    let sum = 0;
                    for (let i = 0; i < dataArray.length; i += 1) {
                        const amplitude = (dataArray[i] - 128) / 128;
                        sum += amplitude * amplitude;
                    }

                    const rms = Math.sqrt(sum / dataArray.length);
                    const instantLevel = Math.min(1, Math.max(0, rms - TRACK_NOISE_FLOOR) * TRACK_LEVEL_GAIN);
                    smoothedLevel =
                        smoothedLevel * (1 - TRACK_LEVEL_SMOOTHING) +
                        instantLevel * TRACK_LEVEL_SMOOTHING;

                    setLocalTrackLevel((previousLevel) =>
                        Math.abs(previousLevel - smoothedLevel) > 0.005 ? smoothedLevel : previousLevel,
                    );
                };

                updateLevel();
                intervalId = window.setInterval(updateLevel, 1000 / 30);
            } catch {
                setLocalTrackLevel(0);
            }
        };

        void start();

        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }
            if (cleanup) {
                void cleanup().catch(() => {});
            }
        };
    }, [audioTrack, trackMediaStream, microphoneEnabled]);

    const hasUsableAudioTrack = Boolean(audioTrack && trackMediaStream);
    const effectiveLevel = hasUsableAudioTrack ? localTrackLevel : level;
    const clampedLevel = Math.max(0, Math.min(effectiveLevel ?? 0, 1));
    const scaledLevel = hasUsableAudioTrack ? clampedLevel : Math.min(1, clampedLevel * 8);

    // Logarithmic scale: quiet speech lights up several segments quickly,
    // but hitting the top requires genuinely loud audio.
    const logLevel = microphoneEnabled
        ? Math.min(1, Math.pow(scaledLevel, hasUsableAudioTrack ? 0.58 : 0.42) * 1.02)
        : 0;
    const activeSegments = Math.round(logLevel * SEGMENT_COUNT);

    const MicIcon = microphoneEnabled ? MicOutlinedIcon : MicOffOutlinedIcon;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
            <MicIcon
                sx={{
                    fontSize: 15,
                    color: microphoneEnabled ? '#10B981' : 'var(--app-text-subtle)',
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    flex: 1,
                    height: 14,
                }}
            >
                {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
                    const isActive = microphoneEnabled && i < activeSegments;
                    return (
                        <Box
                            key={i}
                            sx={{
                                flex: 1,
                                height: '100%',
                                borderRadius: '2px',
                                backgroundColor: isActive
                                    ? getSegmentColor(i)
                                    : 'var(--app-border-subtle)',
                                transition: hasUsableAudioTrack ? 'none' : 'background-color 80ms linear',
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export default ParticipantAudioLevelMeter;
